import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timezone, TypeGuard } from '@principle-theorem/shared';
import { isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';

export const PAYMENT_METHODS_V2_RESOURCE_TYPE = 'paymentMethodV2';

export const PAYMENT_METHODS_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Payment Methods V2 List',
      description: `
    Distinct payment method strings recorded in trans4.trnsref for receipts (trnstype = 86020).
    Values are normalised to UPPER(TRIM(trnsref)).
    Used to drive the payment-method custom mapping so each unique method string can be linked to a brand Manual Transaction Type.
    `,
      idPrefix: PAYMENT_METHODS_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

/**
 * A distinct payment method string extracted from the raw ledger.
 * `id` is the normalised method key (UPPER(TRIM(trnsref))).
 * `label` is the same value used for display.
 */
export interface IExactV2PaymentMethod {
  id: string;
  label: string;
}

export interface IExactV2PaymentMethodFilters {
  methodKey: string;
}

function isExactV2PaymentMethod(
  data: unknown,
  logError: boolean
): data is IExactV2PaymentMethod {
  return TypeGuard.interface<IExactV2PaymentMethod>(
    {
      id: isString,
      label: isString,
    },
    logError
  )(data);
}

/**
 * Queries distinct normalised payment method strings from receipts.
 * Blank / null trnsref values are excluded — those will fall through to
 * "Manual - No Transaction Type" in the builder.
 */
const PAYMENT_METHODS_V2_QUERY = `
SELECT DISTINCT
  UPPER(TRIM(trnsref)) AS id,
  UPPER(TRIM(trnsref)) AS label
FROM trans4
WHERE
  trnstype = 86020
  AND convert_to_decimal(amount) <> 0
  AND NOT COALESCE(_is_deleted, false)
  AND NULLIF(TRIM(trnsref), '') IS NOT NULL
ORDER BY label ASC
${OFFSET_PLACEHOLDER}
`;

export type ExactV2PaymentMethod = ISourceEntityTypes<
  IExactV2PaymentMethod,
  object,
  IExactV2PaymentMethodFilters
>;

export class PaymentMethodsV2SourceEntity extends BaseSourceEntity<ExactV2PaymentMethod> {
  sourceEntity = PAYMENT_METHODS_V2_SOURCE_ENTITY;
  entityResourceType = PAYMENT_METHODS_V2_RESOURCE_TYPE;
  sourceQuery = PAYMENT_METHODS_V2_QUERY;
  verifySourceFn = isExactV2PaymentMethod;

  getSourceRecordId(data: IExactV2PaymentMethod): string {
    return data.id;
  }

  getSourceLabel(record: IExactV2PaymentMethod): string {
    return record.label;
  }

  translate(_data: IExactV2PaymentMethod, _timezone: Timezone): object {
    return {};
  }

  getFilterData(
    data: IExactV2PaymentMethod,
    _timezone: Timezone
  ): IExactV2PaymentMethodFilters {
    return {
      methodKey: data.id,
    };
  }
}
